import React, {useMemo, useRef} from 'react';
import {useRoute} from '@react-navigation/native';

import RecordsScreen from '/component/layout/RecordsScreen';
import {COACH_REPORT} from '/enum/popover.enum';
import {REPORT_CARD_CONTENT_ALL_COACHES} from '/enum/reportCard.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {withHOC} from '/util/hoc.util';

import {ReportProps} from '../Report.types';
import withRecordPopover from '../withRecordPopover';

import {VolumePerformanceContent} from './components/VolumePerformanceContent';
import {styles} from './styles/VolumePerformanceContent.style';
import reportPayload from './reportPayload';

export const VolumePerformanceReport = React.memo(
  ({
    loadingData = {total: 0},
    showRecordPopover = () => {},
    ...props
  }: ReportProps & any) => {
    const route = useRoute();
    const routeParams = (route?.params as any) || {};

    // Recover from multiple possible sources (Route params, direct props, or nested modalProps)
    const VolPerfUserId =
      routeParams.modalProps?.VolPerfUserId || props.VolPerfUserId;

    const VolPerfUserName =
      routeParams.modalProps?.VolPerfUserName ||
      props.VolPerfUserName ||
      'Coach';

    // Stabilize the showRecordPopover function using a ref to prevent re-renders of RecordsScreen
    const showRecordPopoverRef = useRef(showRecordPopover);
    showRecordPopoverRef.current = showRecordPopover;

    const contentProps = useMemo(
      () => ({
        loadingData,
        showRecordPopover: (...args: any[]) =>
          showRecordPopoverRef.current(...args),
        VolPerfUserId,
      }),
      [VolPerfUserId], // Only re-create if the user ID changes
    );

    return (
      <RecordsScreen
        {...reportPayload}
        title={`${VolPerfUserName}'s Volume Performance`}
        isTopLineTitle={true}
        contentProps={contentProps}
        contentComponent={VolumePerformanceContent}
        titleStyle={styles.ReportTitle}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to break the loop. We ignore showRecordPopover changes
    // because we stabilize it with a ref inside the component.
    const getUserId = (p: any) =>
      p.route?.params?.VolPerfUserId ||
      p.route?.params?.modalProps?.VolPerfUserId ||
      p.VolPerfUserId ||
      p.modalProps?.VolPerfUserId;

    return getUserId(prevProps) === getUserId(nextProps);
  },
);

export default withHOC(
  withTranslation('report.VolumePerformance'),
  withRecordPopover({
    reportPayload,
    popoverName: COACH_REPORT,
    popoverContentName: REPORT_CARD_CONTENT_ALL_COACHES,
    popoverContentProps: {
      renderTitle: () =>
        withTranslation.ts(
          'reportRecordTitle.volumePerformance',
          'Volume Performance',
        ),
    },
  }),
)(VolumePerformanceReport);
